/**
 * Layout arithmetic for the floating browser preview.
 *
 * Ported from t3code (`apps/web/src/components/preview/previewMiniPlayerLayout.ts`,
 * MIT, pin `e3e7cc3f`). Adaptations: the mirrored source is forge's session
 * viewport, a concrete `{width, height}`, so upstream's fill-viewport
 * resolution and its device-stream helpers have no forge counterpart and are
 * not ported; resize directions come from forge's own
 * `browserViewportLayout`; the z-index straddle is stated against forge's
 * dialog layer (z-50).
 *
 * @module browserMiniPlayerLayout
 */
import type { BrowserViewportResizeDirection } from "~/browser/browserViewportLayout";

import type { BrowserMiniPlayerPosition, BrowserMiniPlayerSize } from "./browserMiniPlayerStore";

export const BROWSER_MINI_PLAYER_EDGE_GAP = 12;
export const BROWSER_MINI_PLAYER_CORNER_RADIUS = 12;
// The floating shell straddles these: content at 48 and its chrome at 49 sit
// above panel chrome and under the dialog layer (z-50).
export const BROWSER_MINI_PLAYER_BACKDROP_Z_INDEX = 47;
export const BROWSER_MINI_PLAYER_CONTENT_Z_INDEX = 48;
export const BROWSER_MINI_PLAYER_CHROME_Z_INDEX = 49;
// A fresh player is the largest box at the source aspect ratio that fits here.
const BROWSER_MINI_PLAYER_DEFAULT_BOX = { width: 320, height: 320 } as const;
const BROWSER_MINI_PLAYER_MIN_SIZE = { width: 240, height: 150 } as const;

export interface BrowserMiniPlayerFrame extends BrowserMiniPlayerPosition, BrowserMiniPlayerSize {}

/**
 * Whether a mirrored source can seed the layout math at all. The session's
 * viewport is validated upstream as a shape, not as values, so a zero or
 * negative dimension can reach the client; either would divide the aspect
 * ratio into infinity or fold the frame to a non-positive height. The
 * floater hides until the session reports something renderable.
 */
export function isUsableBrowserMiniPlayerSource(size: BrowserMiniPlayerSize): boolean {
  return (
    Number.isFinite(size.width) && Number.isFinite(size.height) && size.width > 0 && size.height > 0
  );
}

interface HorizontalSpan {
  readonly left: number;
  readonly right: number;
}

/**
 * The composer stack docked to the bottom edge, in container coordinates. It
 * only reserves the columns it covers, so the margins beside it stay open all
 * the way down.
 */
export interface BrowserMiniPlayerObstacles {
  readonly composer: (HorizontalSpan & { readonly height: number }) | null;
}

export const NO_BROWSER_MINI_PLAYER_OBSTACLES: BrowserMiniPlayerObstacles = { composer: null };

const spanOf = (x: number, width: number): HorizontalSpan => ({ left: x, right: x + width });

const spansOverlap = (a: HorizontalSpan, b: HorizontalSpan) => a.left < b.right && a.right > b.left;

/** The lowest row (before the edge gap) open to a player covering these columns. */
function floorFor(
  span: HorizontalSpan,
  container: BrowserMiniPlayerSize,
  obstacles: BrowserMiniPlayerObstacles,
): number {
  const { composer } = obstacles;
  return composer && spansOverlap(span, composer)
    ? container.height - Math.max(0, composer.height)
    : container.height;
}

/**
 * The box a stored size is fitted into. A player with a position keeps the
 * rows its own columns have, so a tall frame parked beside the composer
 * survives the next layout pass; without one it takes the rows above the
 * composer, which every column has.
 */
const availableArea = (
  container: BrowserMiniPlayerSize,
  obstacles: BrowserMiniPlayerObstacles,
  span: HorizontalSpan | null,
): BrowserMiniPlayerSize => ({
  width: container.width - BROWSER_MINI_PLAYER_EDGE_GAP * 2,
  height:
    (span
      ? floorFor(span, container, obstacles)
      : container.height - Math.max(0, obstacles.composer?.height ?? 0)) -
    BROWSER_MINI_PLAYER_EDGE_GAP * 2,
});

/**
 * Width is the player's only free dimension; height always follows the source
 * aspect ratio so the mirrored viewport fills the box without letterboxing.
 * The player never grows past the source's own size, and a tight container
 * wins over the minimum.
 */
function fitBrowserMiniPlayerWidth(
  desiredWidth: number,
  source: BrowserMiniPlayerSize,
  max: BrowserMiniPlayerSize,
): BrowserMiniPlayerSize {
  const aspectRatio = source.width / source.height;
  const width = Math.min(
    Math.max(
      desiredWidth,
      BROWSER_MINI_PLAYER_MIN_SIZE.width,
      BROWSER_MINI_PLAYER_MIN_SIZE.height * aspectRatio,
    ),
    source.width,
    Math.max(1, max.width),
    Math.max(1, max.height * aspectRatio),
  );
  return { width: Math.round(width), height: Math.round(width / aspectRatio) };
}

function defaultBrowserMiniPlayerWidth(source: BrowserMiniPlayerSize): number {
  return Math.min(
    BROWSER_MINI_PLAYER_DEFAULT_BOX.width,
    (BROWSER_MINI_PLAYER_DEFAULT_BOX.height * source.width) / source.height,
  );
}

const clampToContainer = (
  position: BrowserMiniPlayerPosition,
  container: BrowserMiniPlayerSize,
  player: BrowserMiniPlayerSize,
  bottom = container.height,
): BrowserMiniPlayerPosition => ({
  x: Math.min(
    Math.max(position.x, BROWSER_MINI_PLAYER_EDGE_GAP),
    Math.max(
      BROWSER_MINI_PLAYER_EDGE_GAP,
      container.width - player.width - BROWSER_MINI_PLAYER_EDGE_GAP,
    ),
  ),
  y: Math.min(
    Math.max(position.y, BROWSER_MINI_PLAYER_EDGE_GAP),
    Math.max(BROWSER_MINI_PLAYER_EDGE_GAP, bottom - player.height - BROWSER_MINI_PLAYER_EDGE_GAP),
  ),
});

const overlapsObstacle = (
  position: BrowserMiniPlayerPosition,
  player: BrowserMiniPlayerSize,
  container: BrowserMiniPlayerSize,
  obstacles: BrowserMiniPlayerObstacles,
): boolean =>
  position.y + player.height > floorFor(spanOf(position.x, player.width), container, obstacles);

/**
 * Keeps the player inside the container and off the composer. An overlapping
 * player is pushed out along whichever side needs the smaller move, so a drag
 * slides along the composer into the margin beside it instead of stopping at
 * its top edge; when no side leaves it fully clear it sits above the composer.
 */
export function clampBrowserMiniPlayerPosition(
  position: BrowserMiniPlayerPosition,
  container: BrowserMiniPlayerSize,
  player: BrowserMiniPlayerSize,
  obstacles: BrowserMiniPlayerObstacles = NO_BROWSER_MINI_PLAYER_OBSTACLES,
): BrowserMiniPlayerPosition {
  const inside = clampToContainer(position, container, player);
  const { composer } = obstacles;
  if (!composer || !overlapsObstacle(inside, player, container, obstacles)) return inside;
  const gap = BROWSER_MINI_PLAYER_EDGE_GAP;
  const above = { x: inside.x, y: container.height - composer.height - gap - player.height };
  const beside = [
    { x: composer.left - gap - player.width, y: inside.y },
    { x: composer.right + gap, y: inside.y },
  ];
  let best = clampToContainer(above, container, player);
  let bestDistance = Math.abs(best.y - inside.y);
  for (const candidate of beside) {
    const clamped = clampToContainer(candidate, container, player);
    if (clamped.x !== candidate.x || overlapsObstacle(candidate, player, container, obstacles)) {
      continue;
    }
    const distance = Math.abs(candidate.x - inside.x);
    if (distance < bestDistance) {
      best = candidate;
      bestDistance = distance;
    }
  }
  return best;
}

/**
 * Resolves the on-screen frame from the stored width and position. Clamping
 * happens here on every layout pass instead of being written back to the
 * store, so a temporarily narrow container never destroys the user's chosen
 * width. A player without a position sits in the top-right corner.
 */
export function resolveBrowserMiniPlayerFrame(input: {
  readonly width: number | null;
  readonly position: BrowserMiniPlayerPosition | null;
  readonly source: BrowserMiniPlayerSize;
  readonly container: BrowserMiniPlayerSize;
  readonly obstacles?: BrowserMiniPlayerObstacles;
}): BrowserMiniPlayerFrame {
  const {
    width,
    position,
    source,
    container,
    obstacles = NO_BROWSER_MINI_PLAYER_OBSTACLES,
  } = input;
  const size = fitBrowserMiniPlayerWidth(
    width ?? defaultBrowserMiniPlayerWidth(source),
    source,
    availableArea(container, obstacles, position && width ? spanOf(position.x, width) : null),
  );
  const anchored = position ?? {
    x: container.width - BROWSER_MINI_PLAYER_EDGE_GAP - size.width,
    y: BROWSER_MINI_PLAYER_EDGE_GAP,
  };
  return { ...clampBrowserMiniPlayerPosition(anchored, container, size, obstacles), ...size };
}

/**
 * How far a resize may grow, per axis. Growth stops where the player's
 * current columns meet the composer, and a plain edge drag lets the free axis
 * use everything those columns have. A wider player may reach new columns;
 * the clamp below slides it clear.
 */
function resizeBounds(
  direction: BrowserViewportResizeDirection,
  start: BrowserMiniPlayerFrame,
  container: BrowserMiniPlayerSize,
  floor: number,
): BrowserMiniPlayerSize {
  const east = direction.includes("east");
  const west = direction.includes("west");
  const north = direction.includes("north");
  const south = direction.includes("south");
  const right = start.x + start.width;
  const bottom = start.y + start.height;
  return {
    width: west
      ? right - BROWSER_MINI_PLAYER_EDGE_GAP
      : east
        ? container.width - BROWSER_MINI_PLAYER_EDGE_GAP - start.x
        : container.width - BROWSER_MINI_PLAYER_EDGE_GAP * 2,
    height: north
      ? bottom - BROWSER_MINI_PLAYER_EDGE_GAP
      : south
        ? floor - BROWSER_MINI_PLAYER_EDGE_GAP - start.y
        : floor - BROWSER_MINI_PLAYER_EDGE_GAP * 2,
  };
}

/**
 * Whether the resize follows the pointer's horizontal demand. A plain edge
 * drag leads on its own axis; a corner drag follows whichever axis moved
 * further relative to its span, so the aspect ratio tracks the gesture.
 */
function resizeLeadsOnWidth(input: {
  readonly direction: BrowserViewportResizeDirection;
  readonly start: BrowserMiniPlayerFrame;
  readonly desiredWidth: number;
  readonly desiredHeight: number;
}): boolean {
  const { direction, start, desiredWidth, desiredHeight } = input;
  const horizontal = direction.includes("east") || direction.includes("west");
  const vertical = direction.includes("north") || direction.includes("south");
  if (horizontal && !vertical) return true;
  if (vertical && !horizontal) return false;
  return (
    Math.abs(desiredWidth - start.width) / start.width >=
    Math.abs(desiredHeight - start.height) / start.height
  );
}

/**
 * Resizes from any edge or corner while holding the aspect ratio. The edge
 * opposite the dragged one stays anchored, so growth stops at the container
 * on that axis and the pointer keeps tracking the grabbed edge. On a plain edge
 * drag the perpendicular axis may use the whole container, and the player
 * shifts as needed to stay inside.
 */
export function resizeBrowserMiniPlayer(input: {
  readonly start: BrowserMiniPlayerFrame;
  readonly direction: BrowserViewportResizeDirection;
  readonly delta: BrowserMiniPlayerPosition;
  readonly source: BrowserMiniPlayerSize;
  readonly container: BrowserMiniPlayerSize;
  readonly obstacles?: BrowserMiniPlayerObstacles;
}): BrowserMiniPlayerFrame {
  const {
    start,
    direction,
    delta,
    source,
    container,
    obstacles = NO_BROWSER_MINI_PLAYER_OBSTACLES,
  } = input;
  const west = direction.includes("west");
  const north = direction.includes("north");
  const right = start.x + start.width;
  const bottom = start.y + start.height;
  const floor = floorFor(spanOf(start.x, start.width), container, obstacles);
  const max = resizeBounds(direction, start, container, floor);
  const desiredWidth = start.width + (direction.includes("east") ? delta.x : west ? -delta.x : 0);
  const desiredHeight =
    start.height + (direction.includes("south") ? delta.y : north ? -delta.y : 0);
  const widthLeads = resizeLeadsOnWidth({ direction, start, desiredWidth, desiredHeight });
  const size = fitBrowserMiniPlayerWidth(
    widthLeads ? desiredWidth : (desiredHeight * source.width) / source.height,
    source,
    max,
  );
  const position = clampBrowserMiniPlayerPosition(
    { x: west ? right - size.width : start.x, y: north ? bottom - size.height : start.y },
    container,
    size,
    obstacles,
  );
  return { ...position, ...size };
}
